/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Button, breakpoints } from '@coursera/cds-core';
import type { ButtonProps } from '@coursera/cds-core';

import withSingleTracked from 'bundles/common/components/withSingleTracked';
import { ENROLL_BUTTON_TESTID } from 'bundles/coursera-plus-common/components/constants';
import type { PropsForEnrollPresentation } from 'bundles/coursera-plus-landing-page/components/CourseraPlusEnrollButton';
import ApiButton from 'bundles/red-shibas-cds/components/ApiButton';

const TrackedCdsButton = withSingleTracked({ type: 'BUTTON' })<ButtonProps>(Button);
const TrackedEnrollButton = withSingleTracked({ type: 'BUTTON' })(ApiButton);

const API_IN_PROGRESS = 'API_IN_PROGRESS';

const emotionStyles = {
  link: css`
    max-width: 420px;
    padding-top: 0;
  `,
  singleBigButton: {
    [`@media (max-width: ${breakpoints.values.sm - 1}px)`]: {
      width: '100%',
    },
  },
  singleBigButtonFullWidthMdAndDown: {
    [`@media (max-width: ${breakpoints.values.md - 1}px)`]: {
      width: '100%',
      maxWidth: 'unset',
    },
  },
  singleBigButtonFullWidthSmAndDown: {
    [`@media (max-width: ${breakpoints.values.sm - 1}px)`]: {
      width: '100%',
      maxWidth: 'unset',
    },
  },
  singleBigButtonSecondary: {
    [`@media (max-width: ${breakpoints.values.sm - 1}px)`]: {
      width: '100%',
      maxWidth: 'unset',
    },
  },
  singleBigButtonSecondaryLessPadding: {
    padding: `var(--cds-spacing-100) var(--cds-spacing-200)`,
    [`@media (max-width: ${breakpoints.values.sm - 1}px)`]: {
      width: '100%',
      maxWidth: 'unset',
    },
  },
  linkWhite: {
    marginTop: '5px',
    fontSize: '16px',
    color: 'white!important',
    fontWeight: 'normal',
    cursor: 'pointer',
    backgroundColor: 'transparent !important',
    borderColor: 'transparent !important',
    ':hover': {
      color: '#eeeeee !important',
    },
  },
};

export const BigButtonNoExpand = (props: PropsForEnrollPresentation) => {
  const { apiStatus, trackingName, onClick } = props;
  return (
    <TrackedEnrollButton
      trackingName={trackingName}
      onClick={onClick}
      variant="primary"
      size="medium"
      label={props.label as string}
      apiStatus={apiStatus}
      data-e2e={ENROLL_BUTTON_TESTID}
      disabled={apiStatus === API_IN_PROGRESS}
    />
  );
};

export const BigButton = (props: PropsForEnrollPresentation) => {
  const { apiStatus, trackingName, onClick } = props;
  return (
    <TrackedEnrollButton
      css={emotionStyles.singleBigButton}
      trackingName={trackingName}
      onClick={onClick}
      variant="primary"
      size="medium"
      label={props.label as string}
      apiStatus={apiStatus}
      data-e2e={ENROLL_BUTTON_TESTID}
      disabled={apiStatus === API_IN_PROGRESS}
    />
  );
};

export const BigButtonFullWidthMdAndDown = (props: PropsForEnrollPresentation) => {
  const { apiStatus, trackingName, onClick } = props;
  return (
    <TrackedEnrollButton
      css={emotionStyles.singleBigButtonFullWidthMdAndDown}
      trackingName={trackingName}
      onClick={onClick}
      variant="primary"
      size="medium"
      label={props.label as string}
      apiStatus={apiStatus}
      data-e2e={ENROLL_BUTTON_TESTID}
      disabled={apiStatus === API_IN_PROGRESS}
    />
  );
};

export const BigButtonFullWidthSmAndDown = (props: PropsForEnrollPresentation) => {
  const { apiStatus, trackingName, onClick } = props;
  return (
    <TrackedEnrollButton
      css={emotionStyles.singleBigButtonFullWidthSmAndDown}
      trackingName={trackingName}
      onClick={onClick}
      variant="primary"
      size="medium"
      label={props.label as string}
      apiStatus={apiStatus}
      data-e2e={ENROLL_BUTTON_TESTID}
      disabled={apiStatus === API_IN_PROGRESS}
    />
  );
};

export const BigButtonSecondary = (props: PropsForEnrollPresentation) => {
  const { apiStatus, trackingName, onClick } = props;
  return (
    <TrackedEnrollButton
      css={emotionStyles.singleBigButtonSecondary}
      trackingName={trackingName}
      onClick={onClick}
      variant="secondary"
      size="medium"
      label={props.label as string}
      apiStatus={apiStatus}
      data-e2e={ENROLL_BUTTON_TESTID}
      disabled={apiStatus === API_IN_PROGRESS}
    />
  );
};

export const BigButtonSecondaryLessPadding = (props: PropsForEnrollPresentation) => {
  const { apiStatus, trackingName, onClick } = props;
  return (
    <TrackedEnrollButton
      css={emotionStyles.singleBigButtonSecondaryLessPadding}
      trackingName={trackingName}
      onClick={onClick}
      variant="secondary"
      size="medium"
      label={props.label as string}
      apiStatus={apiStatus}
      data-e2e={ENROLL_BUTTON_TESTID}
      disabled={apiStatus === API_IN_PROGRESS}
    />
  );
};

export const SmallButton = (props: PropsForEnrollPresentation) => {
  const { apiStatus, trackingName, onClick } = props;
  return (
    <TrackedEnrollButton
      trackingName={trackingName}
      onClick={onClick}
      variant="primary"
      size="small"
      label={props.label as string}
      apiStatus={apiStatus}
      data-e2e={ENROLL_BUTTON_TESTID}
      disabled={apiStatus === API_IN_PROGRESS}
    />
  );
};

export const Link = (props: PropsForEnrollPresentation) => {
  const { apiStatus, trackingName, onClick } = props;

  return (
    <TrackedCdsButton
      disabled={apiStatus === API_IN_PROGRESS}
      loading={apiStatus === API_IN_PROGRESS}
      trackingName={trackingName}
      variant="ghost"
      edgeAlign="start"
      onClick={onClick}
      css={emotionStyles.link}
    >
      {props.label}
    </TrackedCdsButton>
  );
};

export const LinkOnDark = (props: PropsForEnrollPresentation) => {
  const { apiStatus, trackingName, onClick } = props;

  return (
    <TrackedCdsButton
      disabled={apiStatus === API_IN_PROGRESS}
      loading={apiStatus === API_IN_PROGRESS}
      trackingName={trackingName}
      variant="ghostInvert"
      edgeAlign="start"
      onClick={onClick}
      css={emotionStyles.link}
    >
      {props.label}
    </TrackedCdsButton>
  );
};
